// src/components/CreateListingForm/RoommateSelector.jsx
import React, { useState, useEffect, useRef } from "react";
import { BASE_URL } from "../../App";
import styles from "./RoommateSelector.module.css";

export default function RoommateSelector({ value = [], onChange }) {    
    const [query, setQuery] = useState("");
    const [suggestions, setSuggestions] = useState([]);
    const [open, setOpen] = useState(false);
    const abortRef = useRef(null);

    // 🔥 MERGED: username-based search that requires "@"
    useEffect(() => {
        const q = query.trim();

        // Must start with "@"
        if (!q.startsWith("@")) {
            setSuggestions([]);
            return;
        }

        const usernamePart = q.substring(1);
        if (usernamePart.length < 1) {
            setSuggestions([]);
            return;
        }

        const controller = new AbortController();
        abortRef.current?.abort();
        abortRef.current = controller;

        const delay = setTimeout(async () => {
            try {
                const res = await fetch(
                    `${BASE_URL}backend/search_users.php?q=${encodeURIComponent(q)}`,
                    { signal: controller.signal }
                );
                const data = await res.json();

                // Data from backend returns: { id, username }
                setSuggestions(Array.isArray(data) ? data : []);
                setOpen(true);
            } catch {}
        }, 200);

        return () => {
            clearTimeout(delay);
            controller.abort();
        };
    }, [query]);

    // 🔥 Add roommate with username
    const addRoommate = (user) => {
        if (!user) return;

        // Prevent duplicates
        if (value.some((r) => r.id === user.id)) return;

        // Add username + id (replacing teammates' name system)
        onChange([
            ...value,
            { id: user.id, username: user.username },
        ]);

        setQuery("");
        setOpen(false);
    };

    const removeRoommate = (id) => {
        onChange(value.filter((r) => r.id !== id));
    };

    // 🔥 Backend expects "username|id"
    const serialized = value
        .map((r) => `${r.username}|${r.id}`)
        .join(", ");

    return (
        <div className={styles.wrapper}>
            <label className={styles.label}>Current Roommates</label>

            <div className={styles.inputBox}>
                {/* Chips */}
                {value.map((r) => (
                    <span key={r.id} className={styles.chip}>
                        @{r.username}
                        <button type="button" onClick={() => removeRoommate(r.id)}>
                            ×
                        </button>
                    </span>
                ))}

                <input
                    type="text"
                    value={query}
                    placeholder="Type @username…"
                    onChange={(e) => setQuery(e.target.value)}
                    onFocus={() => setOpen(suggestions.length > 0)}
                    onBlur={() => setTimeout(() => setOpen(false), 100)}
                />
            </div>

            {/* Dropdown */}
            {open && suggestions.length > 0 && (
                <ul className={styles.dropdown}>
                    {suggestions.map((u) => (
                        <li key={u.id} onMouseDown={() => addRoommate(u)}>
                            @{u.username}
                        </li>
                    ))}
                </ul>
            )}

            {/* hidden input for backend */}
            <input type="hidden" name="roommates" value={serialized} />
        </div>
    );
}
